import type { TScriptAnimation, TScriptDynamicResource } from '@zep/proto';
import { Direction } from '@zep/types/play/direction';
import type {
  AttackEquipmentItem,
  BaseEquipmentItem,
  DirectionState,
  EffectEquipmentItem,
  EquipmentCatalog,
  EquipmentDirection,
  EquipmentLayer,
  EquipmentUiSkinConfig,
  EquipmentUiSliceInsets,
  EquipmentUiTextConfig,
  IdleBehavior,
  NicknameSkinEquipmentItem,
  PetEquipmentItem,
  PetFollowBehavior,
  PlayerSpriteOverride,
  PlayerSpriteOverrideConfig,
  RideEquipmentItem,
  SpeechBubbleEquipmentItem,
} from '@zep/types/play/equipment';
import {
  DEFAULT_PET_FOLLOW_BEHAVIOR,
  PET_FOLLOW_BEHAVIOR_LIMITS,
} from '@zep/types/play/equipment';

import type {
  EquipmentCatalogResponse,
  InventoryResponse,
  RemoteEquipmentDirectionState,
  RemoteEquipmentIdleBehavior,
  RemoteEquipmentItem,
  RemoteEquipmentLayer,
  RemoteEquipmentLayerAnim,
  RemoteEquipmentUiSkinConfig,
  RemoteEquipmentUiTextConfig,
  RemotePetFollowBehavior,
  RemotePlayerSpriteOverride,
  RemotePlayerSpriteOverrideEntry,
} from './types';

// Plan D·15 — API is now the source of truth. The runtime catalog is built
// entirely from wire data: layer sprite + frame metadata, anims, per-direction
// overlays, idleBehavior, attack params, structured playerSpriteOverride.
// The bundled JSON is no longer consulted at normalize time (it survives as a
// dev-only fallback under D·16). Any drift between admin writes and what
// players see is resolved by `equipment_catalog_version`, not by overlay
// merging against a stale bundle.

export interface NormalizeOptions {
  inventory?: InventoryResponse | null;
}

const DIR_KEY_TO_DIRECTION: Record<string, EquipmentDirection> = {
  left: Direction.LEFT,
  up: Direction.UP,
  right: Direction.RIGHT,
  down: Direction.DOWN,
};

const DEFAULT_RIDE_PLAYER_SPRITE_OVERRIDE: PlayerSpriteOverrideConfig = {
  default: {},
};

const DEFAULT_UI_SKIN_TEXT_CONFIG: Record<
  'speech_bubble' | 'nickname_skin',
  EquipmentUiTextConfig
> = {
  speech_bubble: {
    textBox: { x: 0, y: 0, width: 160, height: 48 },
    padding: { top: 6, right: 10, bottom: 6, left: 10 },
    fontSize: 14,
    fontColor: '#27262E',
    fontWeight: 'semibold',
    textAlign: 'center',
    verticalAlign: 'middle',
    strokeColor: null,
    strokeWidth: 0,
    shadowColor: null,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowBlur: 0,
  },
  nickname_skin: {
    textBox: { x: 0, y: 0, width: 120, height: 24 },
    padding: { top: 2, right: 8, bottom: 2, left: 8 },
    fontSize: 12,
    fontColor: '#FFFFFF',
    fontWeight: 'semibold',
    textAlign: 'center',
    verticalAlign: 'middle',
    strokeColor: null,
    strokeWidth: 0,
    shadowColor: null,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowBlur: 0,
  },
};

function compactDirectionState(
  src: RemoteEquipmentDirectionState | null | undefined,
): DirectionState {
  const out: DirectionState = {};
  if (!src) return out;
  if (src.zIndex != null) out.zIndex = src.zIndex;
  if (src.offsetX != null) out.offsetX = src.offsetX;
  if (src.offsetY != null) out.offsetY = src.offsetY;
  if (src.flipX != null) out.flipX = src.flipX;
  if (src.visible != null) out.visible = src.visible;
  return out;
}

function buildPerDirection(
  raw: Record<string, RemoteEquipmentDirectionState> | null | undefined,
): Partial<Record<EquipmentDirection, Partial<DirectionState>>> | undefined {
  if (!raw) return undefined;
  const out: Partial<Record<EquipmentDirection, Partial<DirectionState>>> = {};
  let touched = false;
  for (const [key, value] of Object.entries(raw)) {
    const dir = DIR_KEY_TO_DIRECTION[key];
    if (dir === undefined) continue;
    out[dir] = compactDirectionState(value);
    touched = true;
  }
  return touched ? out : undefined;
}

function buildAnimationFrames(animation: RemoteEquipmentLayerAnim): number[] {
  if (Array.isArray(animation.frames)) return animation.frames;
  if (
    typeof animation.start !== 'number' ||
    typeof animation.end !== 'number'
  ) {
    return [];
  }

  const step = animation.start <= animation.end ? 1 : -1;
  return Array.from(
    { length: Math.abs(animation.end - animation.start) + 1 },
    (_, index) => animation.start! + index * step,
  );
}

// The catalog API supports compact {start,end} ranges, but TScriptAnimation
// (and every runtime packet using it) supports only explicit frame arrays.
function buildAnims(
  raw: Record<string, RemoteEquipmentLayerAnim> | null,
): Record<string, TScriptAnimation> | undefined {
  if (!raw || Object.keys(raw).length === 0) return undefined;
  return Object.fromEntries(
    Object.entries(raw).map(([key, animation]) => [
      key,
      {
        frames: buildAnimationFrames(animation),
        frameRate: animation.frameRate,
        repeat: animation.repeat,
      },
    ]),
  );
}

function appendImageVersion(path: string, imageVersion: number) {
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}v=${imageVersion}`;
}

function normalizeTextAlign(value: string): EquipmentUiTextConfig['textAlign'] {
  return value === 'left' || value === 'right' || value === 'center'
    ? value
    : 'center';
}

function normalizeVerticalAlign(
  value: string,
): EquipmentUiTextConfig['verticalAlign'] {
  return value === 'top' || value === 'bottom' || value === 'middle'
    ? value
    : 'middle';
}

function cloneUiTextConfig(
  config: EquipmentUiTextConfig,
): EquipmentUiTextConfig {
  return {
    ...config,
    textBox: { ...config.textBox },
    padding: { ...config.padding },
  };
}

function buildUiTextConfig(
  raw: RemoteEquipmentUiTextConfig | null | undefined,
  category: 'speech_bubble' | 'nickname_skin',
): EquipmentUiTextConfig {
  const fallback = DEFAULT_UI_SKIN_TEXT_CONFIG[category];
  if (!raw) return cloneUiTextConfig(fallback);
  return {
    textBox: { ...(raw.textBox ?? fallback.textBox) },
    padding: { ...(raw.padding ?? fallback.padding) },
    fontSize: raw.fontSize ?? fallback.fontSize,
    fontColor: raw.fontColor ?? fallback.fontColor,
    fontWeight: raw.fontWeight ?? fallback.fontWeight,
    textAlign: normalizeTextAlign(raw.textAlign ?? fallback.textAlign),
    verticalAlign: normalizeVerticalAlign(
      raw.verticalAlign ?? fallback.verticalAlign,
    ),
    strokeColor: raw.strokeColor ?? null,
    strokeWidth: raw.strokeWidth ?? fallback.strokeWidth,
    shadowColor: raw.shadowColor ?? null,
    shadowOffsetX: raw.shadowOffsetX ?? fallback.shadowOffsetX,
    shadowOffsetY: raw.shadowOffsetY ?? fallback.shadowOffsetY,
    shadowBlur: raw.shadowBlur ?? fallback.shadowBlur,
  };
}

function buildUiSkinImage(
  raw: RemoteEquipmentUiSkinConfig['spriteImage'] | undefined,
) {
  const url = raw?.url?.trim();
  const version = raw?.version ?? 1;
  return url
    ? {
        url: appendImageVersion(url, version),
        version,
      }
    : null;
}

function buildUiSkinSliceInsets(
  raw: RemoteEquipmentUiSkinConfig['sliceInsets'] | undefined,
): EquipmentUiSliceInsets | null {
  if (!raw) return null;

  const sliceInsets = {
    left: raw.left,
    right: raw.right,
    top: raw.top,
    bottom: raw.bottom,
  };

  return Object.values(sliceInsets).every(
    value => typeof value === 'number' && Number.isFinite(value) && value >= 0,
  )
    ? (sliceInsets as EquipmentUiSliceInsets)
    : null;
}

function buildUiSkinConfig(
  raw: RemoteEquipmentUiSkinConfig | null | undefined,
  category: 'speech_bubble' | 'nickname_skin',
): EquipmentUiSkinConfig {
  return {
    spriteImage: buildUiSkinImage(raw?.spriteImage),
    bodyOffsetX: raw?.bodyOffsetX ?? 0,
    bodyOffsetY: raw?.bodyOffsetY ?? 0,
    sliceInsets: buildUiSkinSliceInsets(raw?.sliceInsets),
    tailImage: buildUiSkinImage(raw?.tailImage),
    tailOffsetX: raw?.tailOffsetX ?? 0,
    tailOffsetY: raw?.tailOffsetY ?? 0,
    textConfig: buildUiTextConfig(raw?.textConfig, category),
  };
}

function buildResource(
  wire: RemoteEquipmentLayer,
  sku: string,
  index: number,
): TScriptDynamicResource {
  // The resource id surfaces the stable per-layer DB surrogate, so the engine
  // can key its composed Phaser textures off it. `name` derives from the SKU
  // + layer index — admins don't set it, but it has to be unique-per-item for
  // Phaser's anim key namespacing.
  return {
    id: wire.id,
    name: index === 0 ? sku : `${sku}_${index}`,
    type: 'png',
    body: appendImageVersion(wire.spritePath, wire.imageVersion),
    imageVersion: wire.imageVersion,
    frameWidth: wire.frameWidth,
    frameHeight: wire.frameHeight,
    anims: buildAnims(wire.anims) ?? {},
  } as TScriptDynamicResource;
}

function buildLayer(
  wire: RemoteEquipmentLayer,
  sku: string,
  index: number,
): EquipmentLayer {
  const perDirection = buildPerDirection(wire.perDirection);
  return {
    resource: buildResource(wire, sku, index),
    default: compactDirectionState(wire.defaultState),
    ...(perDirection ? { perDirection } : {}),
  };
}

function buildOverrideEntry(
  entry: RemotePlayerSpriteOverrideEntry,
): PlayerSpriteOverride {
  const out: PlayerSpriteOverride = {};
  if (entry.offsetX != null) out.offsetX = entry.offsetX;
  if (entry.offsetY != null) out.offsetY = entry.offsetY;
  if (entry.sitOffsetX != null) out.sitOffsetX = entry.sitOffsetX;
  if (entry.sitOffsetY != null) out.sitOffsetY = entry.sitOffsetY;
  if (entry.animation != null) out.animation = entry.animation;
  return out;
}

function buildPlayerSpriteOverride(
  raw: RemotePlayerSpriteOverride,
): PlayerSpriteOverrideConfig {
  const perDirection: Partial<
    Record<EquipmentDirection, Partial<PlayerSpriteOverride>>
  > = {};
  let touched = false;
  if (raw.perDirection) {
    for (const [key, value] of Object.entries(raw.perDirection)) {
      const dir = DIR_KEY_TO_DIRECTION[key];
      if (dir === undefined) continue;
      perDirection[dir] = buildOverrideEntry(value);
      touched = true;
    }
  }
  return {
    default: buildOverrideEntry(raw.default),
    ...(touched ? { perDirection } : {}),
  };
}

function buildIdleBehavior(
  raw: RemoteEquipmentIdleBehavior | null,
): IdleBehavior | undefined {
  if (!raw) return undefined;
  if (raw.periodMs == null || raw.periodMs <= 0) return undefined;

  if (raw.type === 'float') {
    if (raw.amplitudeY == null || raw.amplitudeY <= 0) return undefined;
    return {
      type: 'float',
      amplitudeY: raw.amplitudeY,
      periodMs: raw.periodMs,
    };
  }
  if (raw.type === 'sway') {
    if (raw.amplitudeX == null || raw.amplitudeX <= 0) return undefined;
    return {
      type: 'sway',
      amplitudeX: raw.amplitudeX,
      periodMs: raw.periodMs,
    };
  }
  if (raw.type === 'pulse') {
    const hasScale =
      raw.scaleFrom != null &&
      raw.scaleFrom > 0 &&
      raw.scaleTo != null &&
      raw.scaleTo > 0;
    const hasOpacity =
      raw.opacityFrom != null &&
      raw.opacityFrom >= 0 &&
      raw.opacityTo != null &&
      raw.opacityTo >= 0;
    if (!hasScale && !hasOpacity) return undefined;
    return {
      type: 'pulse',
      ...(hasScale ? { scaleFrom: raw.scaleFrom!, scaleTo: raw.scaleTo! } : {}),
      ...(hasOpacity
        ? { opacityFrom: raw.opacityFrom!, opacityTo: raw.opacityTo! }
        : {}),
      periodMs: raw.periodMs,
    };
  }
  if (raw.type === 'wobble') {
    if (raw.rotationDeg == null || raw.rotationDeg <= 0) return undefined;
    return {
      type: 'wobble',
      rotationDeg: raw.rotationDeg,
      periodMs: raw.periodMs,
    };
  }
  if (raw.type === 'shake') {
    const amplitudeX = raw.amplitudeX ?? undefined;
    const amplitudeY = raw.amplitudeY ?? undefined;
    if ((amplitudeX ?? 0) <= 0 && (amplitudeY ?? 0) <= 0) return undefined;
    return {
      type: 'shake',
      ...(amplitudeX != null ? { amplitudeX } : {}),
      ...(amplitudeY != null ? { amplitudeY } : {}),
      periodMs: raw.periodMs,
    };
  }
  if (raw.type === 'drift') {
    const amplitudeX = raw.amplitudeX ?? undefined;
    const amplitudeY = raw.amplitudeY ?? undefined;
    if ((amplitudeX ?? 0) <= 0 && (amplitudeY ?? 0) <= 0) return undefined;
    return {
      type: 'drift',
      ...(amplitudeX != null ? { amplitudeX } : {}),
      ...(amplitudeY != null ? { amplitudeY } : {}),
      periodMs: raw.periodMs,
    };
  }
  return undefined;
}

function normalizePetTileDistance(
  value: number | null | undefined,
  fallback: number,
  minimum: number,
  maximum: number,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum) {
    return fallback;
  }
  return Math.min(maximum, Math.floor(value));
}

function normalizePetMultiplier(
  value: number | null | undefined,
  fallback: number,
  maximum: number,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return Math.min(maximum, value);
}

function buildPetFollowBehavior(
  raw: RemotePetFollowBehavior | null | undefined,
): PetFollowBehavior {
  const followDistanceTiles = normalizePetTileDistance(
    raw?.followDistanceTiles,
    DEFAULT_PET_FOLLOW_BEHAVIOR.followDistanceTiles,
    PET_FOLLOW_BEHAVIOR_LIMITS.minFollowDistanceTiles,
    PET_FOLLOW_BEHAVIOR_LIMITS.maxFollowDistanceTiles,
  );
  const catchUpDistanceTiles = Math.max(
    followDistanceTiles + 1,
    normalizePetTileDistance(
      raw?.catchUpDistanceTiles,
      DEFAULT_PET_FOLLOW_BEHAVIOR.catchUpDistanceTiles,
      followDistanceTiles + 1,
      PET_FOLLOW_BEHAVIOR_LIMITS.maxCatchUpDistanceTiles,
    ),
  );
  const speedMultiplier = normalizePetMultiplier(
    raw?.speedMultiplier,
    DEFAULT_PET_FOLLOW_BEHAVIOR.speedMultiplier,
    PET_FOLLOW_BEHAVIOR_LIMITS.maxSpeedMultiplier,
  );
  const catchUpSpeedMultiplier = Math.max(
    speedMultiplier,
    normalizePetMultiplier(
      raw?.catchUpSpeedMultiplier,
      DEFAULT_PET_FOLLOW_BEHAVIOR.catchUpSpeedMultiplier,
      PET_FOLLOW_BEHAVIOR_LIMITS.maxCatchUpSpeedMultiplier,
    ),
  );

  return {
    followDistanceTiles,
    catchUpDistanceTiles,
    speedMultiplier,
    catchUpSpeedMultiplier,
  };
}

function buildBaseFields(
  wire: RemoteEquipmentItem,
  ownedIds: ReadonlySet<number>,
): BaseEquipmentItem {
  return {
    id: wire.id,
    sku: wire.sku,
    name: wire.name,
    thumbnailPath: wire.thumbnailPath,
    layers: (wire.layers ?? []).map((l, i) => buildLayer(l, wire.sku, i)),
    acquisitionMode:
      wire.acquisitionMode === 'purchase' ? 'purchase' : 'default',
    priceZem: wire.priceZem,
    price: wire.price ?? null,
    currency: wire.currency ?? 'zem',
    saleStartAt: wire.saleStartAt,
    saleEndAt: wire.saleEndAt,
    isNew: wire.isNew,
    isHot: wire.isHot,
    // Free items are always owned; paid items require an inventory match.
    owned: wire.acquisitionMode === 'free' || ownedIds.has(wire.id),
  };
}

function buildAttackItem(
  wire: RemoteEquipmentItem,
  ownedIds: ReadonlySet<number>,
): AttackEquipmentItem | null {
  if (!wire.attack) return null;
  return {
    ...buildBaseFields(wire, ownedIds),
    category: 'attack',
    attack: {
      type: wire.attack.type,
      param1: wire.attack.param1,
      param2: wire.attack.param2,
    },
  };
}

function buildEffectItem(
  wire: RemoteEquipmentItem,
  ownedIds: ReadonlySet<number>,
): EffectEquipmentItem {
  const idle = buildIdleBehavior(wire.idleBehavior);
  return {
    ...buildBaseFields(wire, ownedIds),
    category: 'effect',
    ...(idle ? { idleBehavior: idle } : {}),
  };
}

function buildRideItem(
  wire: RemoteEquipmentItem,
  ownedIds: ReadonlySet<number>,
): RideEquipmentItem {
  const idle = buildIdleBehavior(wire.idleBehavior);
  return {
    ...buildBaseFields(wire, ownedIds),
    category: 'ride',
    // `playerSpriteOverride` improves the equipped pose/offset, but catalog
    // visibility must not depend on it. Old or partially-backfilled dev DBs can
    // legitimately omit it; the renderer already has a safe `${dir}_sit`
    // convention when the override is empty.
    playerSpriteOverride: wire.playerSpriteOverride
      ? buildPlayerSpriteOverride(wire.playerSpriteOverride)
      : DEFAULT_RIDE_PLAYER_SPRITE_OVERRIDE,
    ...(idle ? { idleBehavior: idle } : {}),
  };
}

function buildPetItem(
  wire: RemoteEquipmentItem,
  ownedIds: ReadonlySet<number>,
): PetEquipmentItem {
  const idle = buildIdleBehavior(wire.idleBehavior);
  return {
    ...buildBaseFields(wire, ownedIds),
    category: 'pet',
    followBehavior: buildPetFollowBehavior(wire.petBehavior),
    ...(idle ? { idleBehavior: idle } : {}),
  };
}

function buildSpeechBubbleItem(
  wire: RemoteEquipmentItem,
  ownedIds: ReadonlySet<number>,
): SpeechBubbleEquipmentItem {
  return {
    ...buildBaseFields(wire, ownedIds),
    category: 'speech_bubble',
    uiSkinConfig: buildUiSkinConfig(wire.uiSkinConfig, 'speech_bubble'),
  };
}

function buildNicknameSkinItem(
  wire: RemoteEquipmentItem,
  ownedIds: ReadonlySet<number>,
): NicknameSkinEquipmentItem {
  return {
    ...buildBaseFields(wire, ownedIds),
    category: 'nickname_skin',
    uiSkinConfig: buildUiSkinConfig(wire.uiSkinConfig, 'nickname_skin'),
  };
}

function buildBucket<T extends BaseEquipmentItem>(
  wireItems: RemoteEquipmentItem[] | undefined,
  bucketName: string,
  build: (wire: RemoteEquipmentItem) => T | null,
): T[] {
  if (!wireItems || wireItems.length === 0) return [];
  const out: T[] = [];
  for (const wire of wireItems) {
    const item = build(wire);
    if (!item) {
      // Server-side per-category invariants prevent this, but log if it ever
      // slips past (e.g. an attack row missing its attack params).
      console.warn(
        `[equipment] dropping ${bucketName} item sku=${wire.sku} (id=${wire.id}) — missing required ${bucketName} metadata`,
      );
      continue;
    }
    out.push(item);
  }
  return out;
}

export function normalizeEquipmentCatalog(
  response: EquipmentCatalogResponse,
  opts: NormalizeOptions = {},
): NormalizedEquipmentCatalog {
  const ownedIds = new Set(opts.inventory?.ownedItemIds ?? []);
  const catalog = {
    catalogVersion: response.catalogVersion,
    attack: buildBucket<AttackEquipmentItem>(
      response.items?.attack,
      'attack',
      wire => buildAttackItem(wire, ownedIds),
    ),
    effect: buildBucket<EffectEquipmentItem>(
      response.items?.effect,
      'effect',
      wire => buildEffectItem(wire, ownedIds),
    ),
    ride: buildBucket<RideEquipmentItem>(response.items?.ride, 'ride', wire =>
      buildRideItem(wire, ownedIds),
    ),
    pet: buildBucket<PetEquipmentItem>(response.items?.pet, 'pet', wire =>
      buildPetItem(wire, ownedIds),
    ),
    speech_bubble: buildBucket<SpeechBubbleEquipmentItem>(
      response.items?.speech_bubble,
      'speech_bubble',
      wire => buildSpeechBubbleItem(wire, ownedIds),
    ),
    nickname_skin: buildBucket<NicknameSkinEquipmentItem>(
      response.items?.nickname_skin,
      'nickname_skin',
      wire => buildNicknameSkinItem(wire, ownedIds),
    ),
  } satisfies NormalizedEquipmentCatalog &
    Record<keyof EquipmentCatalog, unknown>;

  return catalog;
}

export type NormalizedEquipmentCatalog = EquipmentCatalog & {
  catalogVersion: number;
};
